import { ApolloCache, DefaultContext } from '@apollo/client';
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions';
import { isEmpty, isNil } from 'lodash';
import { Queries } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import { MBIconV2Name, MBIconV2NameMap } from '@shared/components/image';
import { API } from '@shared/types/api';
import { INPUT_COMPONENT_TYPE_VALUES, InputComponent, InputComponentType } from './InputComponent';
import { GenericComponent, GenericOverride, RelatedCardComponent } from './config';

export type DisplayableComponent = Exclude<
  API.AnyComponent,
  API.StaticTextComponentFragment | API.InputButtonComponent | API.ReferencedInComponentFragment
>;

export function isDisplayableComponent(
  component: GenericComponent | GenericOverride,
): component is DisplayableComponent {
  return component.type !== 'INPUT_BUTTON' && component.type !== 'STATIC_TEXT';
}

export function isAddressComponent(
  component: GenericComponent | GenericOverride,
): component is API.AddressComponentFragment {
  return component.type === 'ADDRESS';
}

export function isAuditCheckboxComponent(
  component: GenericComponent | GenericOverride,
): component is API.AuditCheckboxComponentFragment {
  return component.type === 'AUDIT_CHECKBOX';
}

export function isAuditTagComponent(
  component: GenericComponent | GenericOverride,
): component is API.AuditTagComponent {
  return component.type === 'AUDIT_TAG';
}

export function isAuditTextComponent(
  component: GenericComponent | GenericOverride,
): component is API.AuditTextComponentFragment {
  return component.type === 'AUDIT_TEXT';
}

export function isCombinedTagsComponent(
  component: GenericComponent | GenericOverride,
): component is API.CombinedTagsComponentFragment {
  return component.type === 'COMBINED_TAGS';
}

export function isDateComponent(
  component: GenericComponent | GenericOverride,
): component is API.DateComponent {
  return component.type === 'DATE';
}

export function isDateRangeComponent(
  component: GenericComponent | GenericOverride,
): component is API.DateRangeComponent {
  return component.type === 'DATE_RANGE';
}

export function isEmailComponent(
  component: GenericComponent | GenericOverride,
): component is API.EmailComponentFragment {
  return component.type === 'EMAIL';
}

export function isUrlComponent(
  component: GenericComponent | GenericOverride,
): component is API.UrlComponent {
  return component.type === 'URL';
}

export function isFileComponent(
  component: GenericComponent | GenericOverride,
): component is API.FileComponent {
  return component.type === 'FILE';
}

export function isGeolocationComponent(
  component: GenericComponent | GenericOverride,
): component is API.GeolocationComponentFragment {
  return component.type === 'GEOLOCATION';
}

export function isNumberComponent(
  component: GenericComponent | GenericOverride,
): component is API.NumberComponent {
  return component.type === 'NUMBER';
}

export function isPersonComponent(
  component: GenericComponent | GenericOverride,
): component is API.PersonComponentFragment {
  return component.type === 'PERSON';
}

export function isScheduledAutomationComponent(
  component: GenericComponent | GenericOverride,
): component is API.ScheduledAutomationComponentFragment {
  return component?.type === 'SCHEDULED_AUTOMATION';
}

export function isRelatedCardComponent(
  component: GenericComponent | GenericOverride,
): component is API.RelatedCardComponent {
  return component?.type === 'RELATED_CARD';
}

export function isRelatedCardLookupComponent(
  component: GenericComponent | GenericOverride,
): component is API.RelatedCardLookupComponent {
  return component?.type === 'RELATED_CARD_LOOKUP';
}

export function isSignatureComponent(
  component: GenericComponent | GenericOverride,
): component is API.SignatureComponent {
  return component.type === 'SIGNATURE';
}

export function isSubformComponent(
  component: GenericComponent | GenericOverride,
): component is API.SubformComponent {
  return component.type === 'SUBFORM';
}

export function isTagComponent(
  component: GenericComponent | GenericOverride,
): component is API.TagComponent {
  return component.type === 'TAG';
}

export interface ChecklistTagComponent extends API.TagComponent {
  checklistEnabled: true;
  checklistOptions: API.TagComponentChecklistOptions;
}

export function isChecklistTagComponent(
  component: API.AnyComponent,
): component is ChecklistTagComponent {
  return (
    isTagComponent(component) &&
    component.checklistEnabled === true &&
    !isNil(component.checklistOptions)
  );
}

export function isInputComponentType(type: API.ComponentType): type is InputComponentType {
  return INPUT_COMPONENT_TYPE_VALUES.includes(type);
}

export function isInputComponent(component: GenericComponent): component is InputComponent {
  return INPUT_COMPONENT_TYPE_VALUES.includes(component.type);
}

export function isManagingInputComponent(component: GenericComponent): boolean {
  return isDateRangeComponent(component);
}

export function isManagedComponent(component: GenericComponent): boolean {
  return !isNil(component.managedByComponentId);
}

export function isSortableComponent(component: API.AnyComponent): boolean {
  return isInputComponent(component) && !!component.isSortable;
}

export function isGroupableComponent(component: API.AnyComponent): boolean {
  return (
    isTagComponent(component) || isPersonComponent(component) || isRelatedCardComponent(component)
  );
}

export function isStaticTextComponent(
  component: GenericComponent | GenericOverride,
): component is API.StaticTextComponentFragment {
  return 'STATIC_TEXT' === component.type;
}
export function isTextComponent(
  component: GenericComponent | GenericOverride,
): component is API.TextComponent {
  return component.type === 'TEXT';
}

export function isTodoComponent(
  component: GenericComponent | GenericOverride,
): component is API.TodoComponent {
  return component.type === 'TODO';
}

export function isInputButtonComponent(
  component: GenericComponent | GenericOverride,
): component is API.InputButtonComponent {
  return component.type === 'INPUT_BUTTON';
}

export function isTimeTrackerComponent(
  component: GenericComponent | GenericOverride,
): component is API.TimeTrackerComponent {
  return component.type === 'TIME_TRACKER';
}

export function isReferencedInComponent(
  component: GenericComponent | GenericOverride,
): component is API.ReferencedInComponentFragment {
  return component.type === 'REFERENCED_IN';
}

export function isEntityBatchComponent(
  component: GenericComponent | GenericOverride,
): component is API.EntityBatchComponentFragment {
  return component.type === 'ENTITY_BATCH';
}

export function isFilterableComponent(component: InputComponent): boolean {
  return !!component.isFilterable && !isEntityBatchComponent(component);
}

export function isLayoutSection(
  item: API.AnyViewTemplateLayoutItem,
): item is API.ViewTemplateLayoutSectionFragment {
  return item.type === 'SECTION';
}

export function isLayoutComponent(
  item: API.AnyViewTemplateLayoutItem,
): item is API.ViewTemplateLayoutComponentFragment {
  return item.type === 'SINGLE_COMPONENT';
}

export function getComponentIcon(component: GenericComponent): MBIconV2Name | undefined {
  if ('icon' in component && !isNil(component.icon) && component.icon in MBIconV2NameMap) {
    return component.icon as MBIconV2Name;
  }
}

export function getComponentLabel(component: GenericComponent): string | undefined {
  if ('label' in component) {
    return component.label ?? undefined;
  }
}

export function getComponentPlaceholder(component: GenericComponent): string | undefined {
  if ('placeholder' in component) {
    return component.placeholder ?? undefined;
  }
}

type ComponentWithSelectableDefault =
  | API.PersonComponentFragment
  | API.RelatedCardComponent
  | API.SubformComponent
  | API.TagComponent;

export function hasSelectableDefault(
  component: API.AnyComponent,
): component is ComponentWithSelectableDefault {
  return (
    isPersonComponent(component) ||
    isRelatedCardComponent(component) ||
    isScheduledAutomationComponent(component) ||
    isSubformComponent(component) ||
    isTagComponent(component) ||
    isAuditTagComponent(component)
  );
}

type ComponentWithoutSelectableDefault = API.EmailComponentFragment;

export function hasDefault(
  component: API.AnyComponent,
): component is ComponentWithoutSelectableDefault {
  if (component.isServerManaged || isEmailComponent(component) || isUrlComponent(component)) {
    return false;
  } else {
    return true;
  }
}

export function hasRelatedCardFilter(component: RelatedCardComponent | undefined) {
  // workflowTemplateId is technically optional, intended to be nonoptional, but can still be null due to previous
  // frontend edge cases. If we can ever guarantee that all workflowTemplateIds are nonnull then we can remove this
  // function, because it is expected that all RelatedCardComponents have at least a workflow template filter. Because
  // we cannot guarantee this today (2024-10-02) we check for null as well other filters to be safe and guard against
  // any misconfigured components.
  //
  // component is also only optional through the legacy (Low Code) workflow.
  return (
    !isNil(component?.workflowTemplateId) || !isEmpty(component?.listDefault?.filterByComponents)
  );
}

export function hasRelatedCardEntityFilter(component: RelatedCardComponent) {
  return !isNil(component.workflowTemplateId);
}

export function getPrimaryNodeAttribute(component: API.AnyComponent): string | undefined {
  if (isAuditTagComponent(component)) {
    return 'tags';
  }
  if (isAuditCheckboxComponent(component)) {
    return 'checked';
  }
  if (isAuditTextComponent(component)) {
    return 'text';
  }
}

export function isAuditComponent(
  component: API.AnyComponent,
): component is
  | API.AuditCheckboxComponentFragment
  | API.AuditTagComponent
  | API.AuditTextComponentFragment {
  return (
    isAuditTagComponent(component) ||
    isAuditCheckboxComponent(component) ||
    isAuditTextComponent(component)
  );
}

export type AuditChangeChildValue =
  | API.AuditCheckboxComponentDataValueFragment
  | API.AuditTagComponentDataValueFragment
  | API.AuditTextComponentDataValueFragment;

export function isAuditChangeCheckboxValue(
  value: AuditChangeChildValue,
): value is API.AuditCheckboxComponentDataValueFragment {
  return 'files' in value && 'notes' in value && 'checked' in value;
}

export function isAuditChangeTagValue(
  value: AuditChangeChildValue,
): value is API.AuditTagComponentDataValueFragment {
  return 'files' in value && 'notes' in value && 'tags' in value;
}

export function isAuditChangeTextValue(
  value: AuditChangeChildValue,
): value is API.AuditTextComponentDataValueFragment {
  return 'text' in value;
}

export function isSubformComponentDataChange(
  change: API.AnyComponentDataChangeFragment,
): change is API.SubformComponentDataChangeFragment {
  return 'componentType' in change && change.componentType === 'SUBFORM';
}

export function isSubformComponentChildComponentDataChange(
  change: API.SubformComponentChildComponentDataChangeFragment,
): change is
  | API.AuditCheckboxComponentDataChangeFragment
  | API.AuditTagComponentDataChangeFragment
  | API.AuditTextComponentDataChangeFragment {
  return (
    'componentType' in change &&
    ['AUDIT_TAG', 'AUDIT_CHECKBOX', 'AUDIT_TEXT'].includes(change.componentType)
  );
}

export async function getLatestWorkflowTemplate(workflowTemplateId: string): Promise<{
  workflowTemplate?: API.WorkflowTemplateFragment;
  loading: boolean;
  error?: Error;
}> {
  const apolloClient = getSharedApolloClient();
  const { data, loading, error } = await apolloClient.query<
    API.GetWorkflowTemplateQuery,
    API.GetWorkflowTemplateQueryVariables
  >({
    query: Queries.workflow.getWorkflowTemplate,
    fetchPolicy: 'network-only',
    variables: {
      id: workflowTemplateId,
    },
  });

  const workflowTemplate = !isNil(data?.getWorkflowTemplate)
    ? (data.getWorkflowTemplate as API.WorkflowTemplateFragment) // CardDefinition mismatch requires casting here
    : undefined;

  return { workflowTemplate, loading, error };
}

export function refetchAndInvalidateQueriesAfterMutatingViewTemplates(
  workflowTemplateId: string | null,
): MutationBaseOptions<any, any, DefaultContext, ApolloCache<any>> {
  return {
    refetchQueries: [
      {
        query: Queries.workflow.getWorkflowTemplate,
        variables: { id: workflowTemplateId },
      },
      Queries.viewTemplate.getViewTemplate,
    ],
    update: cache => {
      cache.modify({
        fields: (fieldValue, { fieldName, storeFieldName, DELETE }) =>
          // This is a workaround because we want to pass `listViewTemplatesV2` to `refetchQueries` but there are no
          // active `listViewTemplatesV2` queries in some places of the app where we allow the user to modify view
          // templates. We explicitly do not cache view templates independently, so the contents of the view templates
          // are cached in the query responses and needs to be managed.
          fieldName === 'listViewTemplatesV2' &&
          (isNil(workflowTemplateId) ||
            storeFieldName.includes(`"workflowTemplateId":"${workflowTemplateId}"`))
            ? DELETE
            : fieldValue,
      });
    },
  };
}
